/**
 * The inline HTML a prose field may carry, and the edits the toolbar makes to it.
 *
 * The structured generator may put `<a href>`, `<strong>` and `<em>` in introduction, every
 * content, every description and the comparison conclusion; the site renders those fields through
 * its sanitiser and strips anything else. So the toolbar offers exactly those three and nothing
 * that would be thrown away on the way to the page.
 */

export interface Wrapped {
  value: string;
  /** Where the selection should sit afterwards: around the wrapped text. */
  selectionStart: number;
  selectionEnd: number;
}

/** Wrap [start, end) in `before`/`after`. An empty selection inserts a placeholder word. */
export function wrapSelection(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  placeholder = 'text',
): Wrapped {
  const lo = Math.max(0, Math.min(start, end, value.length));
  const hi = Math.min(value.length, Math.max(start, end));
  const inner = value.slice(lo, hi) || placeholder;
  const next = value.slice(0, lo) + before + inner + after + value.slice(hi);
  return {
    value: next,
    selectionStart: lo + before.length,
    selectionEnd: lo + before.length + inner.length,
  };
}

/**
 * Whether an href is one the site keeps: a site-relative path or an https URL.
 *
 * `javascript:` and `data:` are what a sanitiser exists to drop, and `http:` is mixed content on
 * an https page. Refusing them here tells the author now rather than leaving a link that silently
 * renders as plain words.
 */
export function isAllowedHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (h.startsWith('/') && !h.startsWith('//')) return true;
  return /^https:\/\/[^\s/$.?#][^\s]*$/i.test(h);
}

const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

export function linkTags(href: string): { before: string; after: string } {
  return { before: `<a href="${escapeAttr(href.trim())}">`, after: '</a>' };
}
